import React from 'react';
import { Calendar, Database, BarChart2, TrendingDown, Repeat, AlertCircle, Dumbbell, UserMinus } from 'lucide-react';

export default function AdminAnalytics({ analytics }) {
    if (!analytics) return null;

    const formatDuration = (mins) => {
        if (!mins) return '0u 0m';
        const h = Math.floor(mins / 60);
        const m = Math.floor(mins % 60);
        return `${h > 0 ? h + 'u ' : ''}${m}m`;
    };

    return (
        <div>
            <h3 className="text-xl font-bold text-blue-400 flex items-center gap-2 mb-4"><BarChart2 size={20}/> Workout Analytics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-gradient-to-br from-blue-900/40 to-black p-6 rounded-2xl border border-blue-500/20 shadow-lg">
                    <h4 className="text-blue-400 font-bold mb-4 flex items-center gap-2"><Calendar size={18}/> Vandaag</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Workouts</span><span className="text-2xl font-black text-white">{analytics.today.workouts}</span></div>
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Tijd</span><span className="text-lg font-bold text-blue-300">{formatDuration(analytics.today.durationMinutes)}</span></div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-900/40 to-black p-6 rounded-2xl border border-purple-500/20 shadow-lg">
                    <h4 className="text-purple-400 font-bold mb-4 flex items-center gap-2"><Calendar size={18}/> Deze Week</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Workouts</span><span className="text-2xl font-black text-white">{analytics.week.workouts}</span></div>
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Tijd</span><span className="text-lg font-bold text-purple-300">{formatDuration(analytics.week.durationMinutes)}</span></div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-orange-900/40 to-black p-6 rounded-2xl border border-orange-500/20 shadow-lg">
                    <h4 className="text-orange-400 font-bold mb-4 flex items-center gap-2"><Calendar size={18}/> Deze Maand</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Workouts</span><span className="text-2xl font-black text-white">{analytics.month.workouts}</span></div>
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Tijd</span><span className="text-lg font-bold text-orange-300">{formatDuration(analytics.month.durationMinutes)}</span></div>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-900/40 to-black p-6 rounded-2xl border border-green-500/20 shadow-lg">
                    <h4 className="text-green-400 font-bold mb-4 flex items-center gap-2"><Database size={18}/> All-Time</h4>
                    <div className="space-y-2">
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Workouts</span><span className="text-2xl font-black text-white">{analytics.totals.workouts}</span></div>
                        <div className="flex justify-between items-end"><span className="text-gray-400 text-sm">Tijd</span><span className="text-lg font-bold text-green-300">{formatDuration(analytics.totals.durationMinutes)}</span></div>
                    </div>
                </div>
            </div>
            
            <div className="bg-black/30 p-6 rounded-xl border border-white/5 flex items-center justify-between mb-8">
                <span className="text-gray-400 font-bold text-lg">Actieve Atleten in MyWoD</span>
                <span className="bg-white/10 px-4 py-2 rounded-lg text-white font-black text-xl">{analytics.activeUsers}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Retentie Radar */}
                <div>
                    <h3 className="text-xl font-bold text-red-400 flex items-center gap-2 mb-4"><UserMinus size={20}/> Retentie Radar</h3>
                    {(!analytics.inactiveUsers || analytics.inactiveUsers.length === 0) ? (
                        <div className="bg-black/30 p-8 rounded-xl border border-white/5 text-center text-gray-500 italic">Iedereen is lekker bezig! Geen 'slapende' atleten gevonden.</div>
                    ) : (
                        <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-[#18181b]">
                                    <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                                        <th className="py-4 px-4">Atleet</th>
                                        <th className="py-4 px-4 text-right">Dagen Inactief</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {analytics.inactiveUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-white/5 transition-colors">
                                            <td className="py-4 px-4 font-bold text-white flex items-center gap-2"><AlertCircle size={14} className="text-red-500"/> {user.name}</td>
                                            <td className="py-4 px-4 text-right font-black text-red-400">{user.days_inactive} dagen</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Equipment Coverage */}
                <div>
                    <h3 className="text-xl font-bold text-orange-400 flex items-center gap-2 mb-4"><Dumbbell size={20}/> Home Gym Dekkingsgraad</h3>
                    {(!analytics.equipmentCoverage || analytics.equipmentCoverage.length === 0) ? (
                        <div className="bg-black/30 p-8 rounded-xl border border-white/5 text-center text-gray-500 italic">Nog geen apparatuur data van atleten verzameld.</div>
                    ) : (
                        <div className="bg-black/40 p-6 rounded-xl border border-white/5 space-y-4">
                            <div className="text-xs text-gray-500 mb-4 uppercase tracking-widest">Gebaseerd op {analytics.totalHomeUsers} Home Atleten</div>
                            {analytics.equipmentCoverage.map((item, idx) => (
                                <div key={idx} className="relative">
                                    <div className="flex justify-between items-end mb-1">
                                        <span className="text-sm font-bold text-gray-200">{item.name}</span>
                                        <span className="text-xs font-black text-orange-400">{item.percentage}% <span className="text-gray-600 font-normal">({item.count})</span></span>
                                    </div>
                                    <div className="w-full bg-black/60 h-2.5 rounded-full border border-white/5 overflow-hidden">
                                        <div className="bg-gradient-to-r from-orange-600 to-orange-400 h-full rounded-full" style={{ width: `${item.percentage}%` }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <h3 className="text-xl font-bold text-blue-400 flex items-center gap-2 mb-4"><TrendingDown size={20}/> Trainingsgedrag & Swap Trends</h3>
            {(!analytics.swapTrends || analytics.swapTrends.length === 0) ? (
                <div className="bg-black/30 p-8 rounded-xl border border-white/5 text-center text-gray-500 italic">Er zijn nog geen oefeningen geswapt door atleten.</div>
            ) : (
                <div className="bg-black/40 rounded-xl border border-white/5 overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-[#18181b]">
                            <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                                <th className="py-4 px-4">Vermeden Oefening</th>
                                <th className="py-4 px-4">Aantal Swaps</th>
                                <th className="py-4 px-4 text-center">Home / Gym</th>
                                <th className="py-4 px-4">Meest Gekozen Alternatief</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {analytics.swapTrends.map((swap, idx) => (
                                <tr key={idx} className="hover:bg-white/5 transition-colors">
                                    <td className="py-4 px-4 font-bold text-red-400 flex items-center gap-2"><Repeat size={14} className="text-gray-500"/> {swap.original_exercise}</td>
                                    <td className="py-4 px-4 font-black text-white">{swap.times_swapped}x</td>
                                    <td className="py-4 px-4 text-center">
                                        <div className="flex justify-center gap-2 text-xs font-bold">
                                            {swap.home_swaps > 0 && <span className="bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20">H: {swap.home_swaps}</span>}
                                            {swap.gym_swaps > 0 && <span className="bg-purple-900/30 text-purple-400 px-2 py-0.5 rounded border border-purple-500/20">G: {swap.gym_swaps}</span>}
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-green-400 font-bold">{swap.top_alternative || 'Wisselend'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
